/**
 * VÉRAULT Travels - SMART ITINERARY BUILDER
 * 
 * HARDENED UX + AI WRITE-BACK + MOBILE-FIRST
 * 
 * Key Features:
 * 1. Day Navigator Strip (sticky, mobile-first)
 * 2. Day Status States (Draft/Proposed/Approved/Booked) with lock/unlock
 * 3. Day Completeness Checklist
 * 4. Event Card Progressive Disclosure (collapsed/expanded)
 * 5. Alternates Drawer (max 2 per slot)
 * 6. Mobile Optimization (single-column, tap-friendly)
 * 7. Time Blocks: Morning, Midday, Afternoon, Evening, After Dinner
 * 8. Meal Slots: Breakfast, Lunch, Dinner (toggleable)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, Check, X, ChevronDown, ChevronUp, Clock, MapPin, 
  Sparkles, Edit2, Lock, Unlock, Trash2, AlertCircle,
  ExternalLink, Phone, DollarSign, Calendar, Sun, 
  Sunrise, Sunset, Moon, GripVertical, Copy, Save, Loader2,
  Move, ArrowUpDown, Coffee, UtensilsCrossed, Wine, Star,
  Hotel, Car, Plane, Image, FileText, CheckCircle2, Circle,
  ChevronLeft, ChevronRight, MoreHorizontal, Eye, EyeOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { getTripStartDate } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// CONFIGURATION
// ============================================================

// Time blocks with icons and colors
const TIME_BLOCKS = {
  morning: { 
    label: 'Morning', 
    icon: Sunrise, 
    color: 'bg-amber-50 border-amber-200',
    accent: 'text-amber-600',
    times: ['7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM']
  },
  midday: { 
    label: 'Midday', 
    icon: Sun, 
    color: 'bg-sky-50 border-sky-200',
    accent: 'text-sky-600',
    times: ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM']
  },
  afternoon: { 
    label: 'Afternoon', 
    icon: Sunset, 
    color: 'bg-emerald-50 border-emerald-200',
    accent: 'text-emerald-600',
    times: ['2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM']
  },
  evening: { 
    label: 'Evening', 
    icon: Moon, 
    color: 'bg-violet-50 border-violet-200',
    accent: 'text-violet-600',
    times: ['6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM']
  },
  after_dinner: { 
    label: 'After Dinner', 
    icon: Star, 
    color: 'bg-indigo-50 border-indigo-200',
    accent: 'text-indigo-600',
    times: ['10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM']
  }
};

// Meal slots (toggleable per day)
const MEAL_SLOTS = {
  breakfast: { label: 'Breakfast', icon: Coffee, color: 'bg-orange-50 border-orange-200', accent: 'text-orange-600', maxOptions: 2 },
  lunch: { label: 'Lunch', icon: UtensilsCrossed, color: 'bg-green-50 border-green-200', accent: 'text-green-600', maxOptions: 2 },
  dinner: { label: 'Dinner', icon: Wine, color: 'bg-rose-50 border-rose-200', accent: 'text-rose-600', maxOptions: 3 }
};

// Item types with icons
const ITEM_TYPES = {
  activity: { label: 'Activity', icon: Star, color: 'text-blue-600', bg: 'bg-blue-100' },
  restaurant: { label: 'Restaurant', icon: UtensilsCrossed, color: 'text-orange-600', bg: 'bg-orange-100' },
  hotel: { label: 'Hotel', icon: Hotel, color: 'text-purple-600', bg: 'bg-purple-100' },
  experience: { label: 'Experience', icon: Sparkles, color: 'text-pink-600', bg: 'bg-pink-100' },
  transfer: { label: 'Transfer', icon: Car, color: 'text-gray-600', bg: 'bg-gray-100' },
  flight: { label: 'Flight', icon: Plane, color: 'text-sky-600', bg: 'bg-sky-100' },
  free_time: { label: 'Free Time', icon: Coffee, color: 'text-green-600', bg: 'bg-green-100' },
};

// Day status states
const DAY_STATUS = {
  draft: { 
    label: 'Draft', 
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: Circle,
    description: 'Incomplete planning'
  },
  proposed: { 
    label: 'Proposed', 
    color: 'bg-[#C8A85A]/10 text-[#C8A85A] border-[#C8A85A]/30',
    icon: Sparkles,
    description: 'AI suggestions pending'
  },
  approved: { 
    label: 'Approved', 
    color: 'bg-[#8DA399]/10 text-[#8DA399] border-[#8DA399]/30',
    icon: CheckCircle2,
    description: 'Confirmed by advisor'
  },
  booked: { 
    label: 'Booked', 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    icon: Check,
    description: 'Reservations confirmed'
  }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function formatDayDate(startDate, dayNumber) {
  if (!startDate) return 'Date TBD';
  try {
    const date = new Date(startDate);
    date.setDate(date.getDate() + (dayNumber - 1));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return 'Date TBD';
  }
}

function getShortDate(startDate, dayNumber) {
  if (!startDate) return '';
  try {
    const date = new Date(startDate);
    date.setDate(date.getDate() + (dayNumber - 1));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// Calculate day completeness
function calculateDayCompleteness(dayItems, dayNumber, totalDays) {
  const isFirstDay = dayNumber === 1;
  const isLastDay = dayNumber === totalDays;
  
  const checklist = {
    lodging: { expected: !isLastDay, items: dayItems.filter(i => i.type === 'hotel'), status: 'missing' },
    activities: { expected: true, items: dayItems.filter(i => i.type === 'activity' || i.type === 'experience'), status: 'missing' },
    breakfast: { expected: !isFirstDay, items: dayItems.filter(i => i.meal_type === 'breakfast'), status: 'missing' },
    lunch: { expected: true, items: dayItems.filter(i => i.meal_type === 'lunch'), status: 'missing' },
    dinner: { expected: true, items: dayItems.filter(i => i.meal_type === 'dinner'), status: 'missing' },
    transfers: { expected: isFirstDay || isLastDay, items: dayItems.filter(i => i.type === 'transfer' || i.type === 'flight'), status: 'missing' },
  };
  
  Object.keys(checklist).forEach(key => {
    const item = checklist[key];
    if (!item.expected) {
      item.status = 'not_applicable';
    } else if (item.items.length === 0) {
      item.status = 'missing';
    } else if (item.items.some(i => i.intentionally_open)) {
      item.status = 'intentionally_open';
    } else if (item.items.length > 0) {
      const hasApproved = item.items.some(i => i.status === 'approved' || i.status === 'booked');
      item.status = hasApproved ? 'complete' : 'partial';
    }
  });
  
  return checklist;
}

// ============================================================
// DAY NAVIGATOR STRIP (STICKY, MOBILE-FIRST)
// ============================================================
function DayNavigator({ 
  totalDays, 
  startDate, 
  currentDay, 
  onDaySelect, 
  dayStatuses,
  dayCompleteness 
}) {
  const scrollRef = useRef(null);
  
  // Auto-scroll to current day
  useEffect(() => {
    if (scrollRef.current && currentDay) {
      const dayElement = scrollRef.current.querySelector(`[data-day="${currentDay}"]`);
      if (dayElement) {
        dayElement.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentDay]);
  
  return (
    <div 
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E6E4DE] shadow-sm"
      data-testid="day-navigator"
    >
      <div 
        ref={scrollRef}
        className="flex overflow-x-auto gap-1 p-2 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {Array.from({ length: totalDays }, (_, i) => i + 1).map(day => {
          const status = dayStatuses[day] || 'draft';
          const statusConfig = DAY_STATUS[status];
          const isSelected = day === currentDay;
          const shortDate = getShortDate(startDate, day);
          const completeness = dayCompleteness[day] || {};
          
          // Calculate completeness indicator
          const completeCount = Object.values(completeness).filter(c => c.status === 'complete').length;
          const expectedCount = Object.values(completeness).filter(c => c.expected).length;
          
          return (
            <button
              key={day}
              data-day={day}
              onClick={() => onDaySelect(day)}
              className={`flex-shrink-0 min-w-[60px] px-3 py-2 rounded-lg transition-all ${
                isSelected 
                  ? 'bg-[#1A1A1A] text-white shadow-lg scale-105' 
                  : `${statusConfig.color} hover:opacity-80`
              }`}
              data-testid={`day-nav-${day}`}
            >
              <div className="text-center">
                <div className={`text-sm font-semibold ${isSelected ? 'text-white' : ''}`}>
                  Day {day}
                </div>
                <div className={`text-[10px] ${isSelected ? 'text-white/70' : 'opacity-60'}`}>
                  {shortDate}
                </div>
                {/* Status indicator */}
                <div className="flex items-center justify-center gap-0.5 mt-1">
                  {status === 'approved' && <Check className="w-3 h-3" />}
                  {status === 'booked' && <CheckCircle2 className="w-3 h-3" />}
                  {status === 'proposed' && <Sparkles className="w-3 h-3" />}
                  {expectedCount > 0 && (
                    <span className="text-[9px]">{completeCount}/{expectedCount}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// DAY COMPLETENESS CHECKLIST (GLANCEABLE)
// ============================================================
function DayChecklist({ checklist, onJumpToSection }) {
  const items = [
    { key: 'lodging', label: 'Lodging', icon: Hotel },
    { key: 'activities', label: 'Activities', icon: Star },
    { key: 'breakfast', label: 'Breakfast', icon: Coffee },
    { key: 'lunch', label: 'Lunch', icon: UtensilsCrossed },
    { key: 'dinner', label: 'Dinner', icon: Wine },
    { key: 'transfers', label: 'Transport', icon: Car },
  ];
  
  return (
    <div className="flex flex-wrap gap-2 p-2 bg-[#F9F8F4] rounded-lg" data-testid="day-checklist">
      {items.map(({ key, label, icon: Icon }) => {
        const item = checklist[key];
        if (!item || item.status === 'not_applicable') return null;
        
        const statusStyles = {
          complete: 'bg-[#8DA399]/20 text-[#8DA399] border-[#8DA399]/30',
          partial: 'bg-[#C8A85A]/20 text-[#C8A85A] border-[#C8A85A]/30',
          missing: 'bg-gray-100 text-gray-400 border-gray-200',
          intentionally_open: 'bg-blue-50 text-blue-500 border-blue-200'
        };
        
        return (
          <button
            key={key}
            onClick={() => onJumpToSection?.(key)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors hover:opacity-80 ${statusStyles[item.status]}`}
            title={`${label}: ${item.status}`}
          >
            {item.status === 'complete' && <Check className="w-3 h-3" />}
            {item.status === 'partial' && <Circle className="w-3 h-3" />}
            {item.status === 'missing' && <AlertCircle className="w-3 h-3" />}
            <Icon className="w-3 h-3" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// EVENT CARD (PROGRESSIVE DISCLOSURE)
// ============================================================
function EventCard({ 
  item, 
  onUpdate, 
  onDelete, 
  onApprove,
  onSetIntentionallyOpen,
  isDragging = false,
  isAlternate = false
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: item.id,
    data: { item, type: 'item' }
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };
  
  const itemType = ITEM_TYPES[item.type] || ITEM_TYPES.activity;
  const statusConfig = DAY_STATUS[item.status] || DAY_STATUS.draft;
  const Icon = itemType.icon;
  
  useEffect(() => {
    setForm(item);
  }, [item]);
  
  const handleFieldChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, form);
      setEditing(false);
      setExpanded(false);
      toast.success('Saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    setForm(item);
    setEditing(false);
  };

  // COLLAPSED VIEW (Fast Scan)
  if (!expanded) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group bg-white rounded-xl border ${
          isAlternate ? 'border-dashed border-gray-300' : 'border-[#E6E4DE]'
        } shadow-sm hover:shadow-md transition-all ${
          item.status === 'proposed' ? 'ring-2 ring-[#C8A85A]/20' : ''
        }`}
        data-testid={`event-card-${item.id}`}
      >
        <div className="p-3 sm:p-4">
          <div className="flex items-start gap-3">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="mt-1 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <GripVertical className="w-4 h-4" />
            </button>
            
            {/* Icon */}
            <div className={`w-10 h-10 rounded-lg ${itemType.bg} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${itemType.color}`} />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="font-medium text-[#1A1A1A] truncate">{item.title || 'Untitled'}</h4>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-[#8C8C8C]">
                    {item.time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {item.time}
                      </span>
                    )}
                    {item.neighborhood && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {item.neighborhood}
                      </span>
                    )}
                    {item.price_amount && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        {item.price_amount} {item.currency || 'EUR'}
                      </span>
                    )}
                  </div>
                  {item.highlight && (
                    <p className="mt-1 text-sm text-[#595959] line-clamp-1">{item.highlight}</p>
                  )}
                </div>
                
                {/* Status + Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 text-[10px] rounded-full border ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                  
                  {item.website_url && (
                    <a 
                      href={item.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-[#C8A85A] hover:bg-[#C8A85A]/10 rounded"
                      onClick={e => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  
                  <button
                    onClick={() => setExpanded(true)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EXPANDED VIEW (Full Data Fields)
  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ height: 'auto' }}
      animate={{ height: 'auto' }}
      className={`bg-white rounded-xl border ${
        isAlternate ? 'border-dashed border-gray-300' : 'border-[#E6E4DE]'
      } shadow-md`}
      data-testid={`event-card-expanded-${item.id}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-[#E6E4DE] bg-[#F9F8F4]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${itemType.bg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${itemType.color}`} />
            </div>
            {editing ? (
              <Input
                value={form.title || ''}
                onChange={e => handleFieldChange('title', e.target.value)}
                className="font-medium"
                placeholder="Title"
              />
            ) : (
              <h4 className="font-medium text-[#1A1A1A]">{item.title}</h4>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs rounded-full border ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
            <button
              onClick={() => setExpanded(false)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Image Banner - from Smart Suggestions */}
        {item.image_url && (
          <div className="relative h-32 -mx-4 -mt-4 mb-4 bg-[#F5F1EB] overflow-hidden">
            <img 
              src={item.image_url} 
              alt={item.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            {item.price_per_person && (
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-white/90 rounded text-xs font-medium text-[#1A1A1A]">
                {item.price_per_person} per person
              </div>
            )}
          </div>
        )}
        
        {/* Quick Info Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#8C8C8C]">Time</label>
            {editing ? (
              <Input
                value={form.time || ''}
                onChange={e => handleFieldChange('time', e.target.value)}
                placeholder="e.g., 10:00 AM"
                className="mt-1"
              />
            ) : (
              <p className="text-sm font-medium">{item.time || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-[#8C8C8C]">Neighborhood</label>
            {editing ? (
              <Input
                value={form.neighborhood || ''}
                onChange={e => handleFieldChange('neighborhood', e.target.value)}
                placeholder="e.g., Le Marais"
                className="mt-1"
              />
            ) : (
              <p className="text-sm font-medium">{item.neighborhood || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="text-xs text-[#8C8C8C]">Price</label>
            {editing ? (
              <div className="flex gap-1 mt-1">
                <Input
                  type="number"
                  value={form.price_amount || ''}
                  onChange={e => handleFieldChange('price_amount', e.target.value)}
                  placeholder="0"
                  className="w-20"
                />
                <Input
                  value={form.currency || 'EUR'}
                  onChange={e => handleFieldChange('currency', e.target.value)}
                  className="w-16"
                />
              </div>
            ) : (
              <p className="text-sm font-medium">
                {item.price_amount ? `${item.price_amount} ${item.currency || 'EUR'}` : 'Not set'}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-[#8C8C8C]">Type</label>
            {editing ? (
              <Select value={form.type} onValueChange={v => handleFieldChange('type', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ITEM_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-medium">{itemType.label}</p>
            )}
          </div>
        </div>
        
        {/* Highlight */}
        <div>
          <label className="text-xs text-[#8C8C8C]">Highlight</label>
          {editing ? (
            <Input
              value={form.highlight || ''}
              onChange={e => handleFieldChange('highlight', e.target.value)}
              placeholder="One-line description"
              className="mt-1"
            />
          ) : (
            <p className="text-sm mt-1">{item.highlight || 'No description'}</p>
          )}
        </div>
        
        {/* Address */}
        <div>
          <label className="text-xs text-[#8C8C8C]">Address</label>
          {editing ? (
            <Input
              value={form.address || ''}
              onChange={e => handleFieldChange('address', e.target.value)}
              placeholder="Full address"
              className="mt-1"
            />
          ) : (
            <p className="text-sm mt-1 flex items-start gap-1">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#8C8C8C]" />
              {item.address || 'No address'}
            </p>
          )}
        </div>
        
        {/* Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8C8C8C]">Official Website</label>
            {editing ? (
              <Input
                value={form.website_url || ''}
                onChange={e => handleFieldChange('website_url', e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            ) : item.website_url ? (
              <a 
                href={item.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#C8A85A] hover:underline flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                Visit Website
              </a>
            ) : (
              <p className="text-sm text-[#8C8C8C] mt-1">Not set</p>
            )}
          </div>
          <div>
            <label className="text-xs text-[#8C8C8C]">Booking Link</label>
            {editing ? (
              <Input
                value={form.booking_url || ''}
                onChange={e => handleFieldChange('booking_url', e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            ) : item.booking_url ? (
              <a 
                href={item.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#8DA399] hover:underline flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                Book Now
              </a>
            ) : (
              <p className="text-sm text-[#8C8C8C] mt-1">Not set</p>
            )}
          </div>
        </div>
        
        {/* Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8C8C8C]">Phone</label>
            {editing ? (
              <Input
                value={form.phone || ''}
                onChange={e => handleFieldChange('phone', e.target.value)}
                placeholder="+33 1 00 00 00 00"
                className="mt-1"
              />
            ) : (
              <p className="text-sm mt-1 flex items-center gap-1">
                <Phone className="w-3 h-3 text-[#8C8C8C]" />
                {item.phone || 'Not set'}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-[#8C8C8C]">Hours</label>
            {editing ? (
              <Input
                value={form.hours || ''}
                onChange={e => handleFieldChange('hours', e.target.value)}
                placeholder="Mon-Sat 9am-10pm"
                className="mt-1"
              />
            ) : (
              <p className="text-sm mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3 text-[#8C8C8C]" />
                {item.hours || 'Not set'}
              </p>
            )}
          </div>
        </div>
        
        {/* Notes */}
        <div>
          <label className="text-xs text-[#8C8C8C]">Notes</label>
          {editing ? (
            <Textarea
              value={form.notes || ''}
              onChange={e => handleFieldChange('notes', e.target.value)}
              placeholder="Additional details, reservation notes..."
              className="mt-1"
              rows={3}
            />
          ) : (
            <p className="text-sm mt-1 text-[#595959]">{item.notes || 'No notes'}</p>
          )}
        </div>
        
        {/* Confirmation Number */}
        {(item.confirmation_number || editing) && (
          <div>
            <label className="text-xs text-[#8C8C8C]">Confirmation #</label>
            {editing ? (
              <Input
                value={form.confirmation_number || ''}
                onChange={e => handleFieldChange('confirmation_number', e.target.value)}
                placeholder="Booking confirmation"
                className="mt-1"
              />
            ) : (
              <p className="text-sm mt-1 font-mono bg-[#F9F8F4] px-2 py-1 rounded">
                {item.confirmation_number}
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Action Bar */}
      <div className="px-4 py-3 border-t border-[#E6E4DE] bg-[#F9F8F4] flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#8DA399] hover:bg-[#7A9389]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              {item.status === 'proposed' && (
                <Button size="sm" onClick={() => onApprove?.(item.id)} className="bg-[#8DA399] hover:bg-[#7A9389]">
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
              )}
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => onSetIntentionallyOpen?.(item.id, !item.intentionally_open)}
            className={item.intentionally_open ? 'text-blue-500' : 'text-gray-400'}
            title="Mark as intentionally open"
          >
            {item.intentionally_open ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => onDelete?.(item.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// TIME BLOCK SECTION
// ============================================================
function TimeBlockSection({ 
  blockKey, 
  block, 
  items, 
  alternates,
  dayNumber,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onApproveItem,
  onSetIntentionallyOpen
}) {
  const [showAlternates, setShowAlternates] = useState(false);
  const Icon = block.icon;
  const blockItems = items.filter(i => i.section === blockKey);
  const blockAlternates = alternates.filter(a => a.section === blockKey);
  const hasAlternates = blockAlternates.length > 0;
  
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dayNumber}-${blockKey}`,
    data: { day: dayNumber, section: blockKey }
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`rounded-xl border ${block.color} p-3 sm:p-4 transition-all ${
        isOver ? 'ring-2 ring-[#C8A85A] bg-[#C8A85A]/5' : ''
      }`}
      data-testid={`time-block-${blockKey}`}
    >
      {/* Block Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${block.accent}`} />
          <span className={`font-medium ${block.accent}`}>{block.label}</span>
          <span className="text-xs text-gray-500">({blockItems.length} items)</span>
        </div>
        
        <div className="flex items-center gap-2">
          {hasAlternates && (
            <button
              onClick={() => setShowAlternates(!showAlternates)}
              className="text-xs text-[#C8A85A] hover:underline flex items-center gap-1"
            >
              {showAlternates ? 'Hide' : 'Show'} {blockAlternates.length} alternates
              <ChevronDown className={`w-3 h-3 transition-transform ${showAlternates ? 'rotate-180' : ''}`} />
            </button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onAddItem(dayNumber, blockKey)}
            className="h-7 px-2"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Primary Items */}
      <SortableContext items={blockItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {blockItems.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
              No items yet
            </div>
          ) : (
            blockItems.map(item => (
              <EventCard
                key={item.id}
                item={item}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
                onApprove={onApproveItem}
                onSetIntentionallyOpen={onSetIntentionallyOpen}
              />
            ))
          )}
        </div>
      </SortableContext>
      
      {/* Alternates Drawer */}
      <AnimatePresence>
        {showAlternates && blockAlternates.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-3 pt-3 border-t border-dashed border-gray-300"
          >
            <p className="text-xs text-gray-500 mb-2">Alternates (max 2)</p>
            <div className="space-y-2">
              {blockAlternates.slice(0, 2).map(item => (
                <EventCard
                  key={item.id}
                  item={item}
                  isAlternate
                  onUpdate={onUpdateItem}
                  onDelete={onDeleteItem}
                  onApprove={onApproveItem}
                  onSetIntentionallyOpen={onSetIntentionallyOpen}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================
// DAY CARD
// ============================================================
function DayCard({ 
  dayNumber, 
  totalDays,
  startDate,
  items,
  alternates,
  status,
  isLocked,
  onStatusChange,
  onLockToggle,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onApproveItem,
  onSetIntentionallyOpen,
  onOptimizeRoute,
  checklist
}) {
  const [mealSlots, setMealSlots] = useState({ breakfast: true, lunch: true, dinner: true });
  const statusConfig = DAY_STATUS[status] || DAY_STATUS.draft;
  const formattedDate = formatDayDate(startDate, dayNumber);
  const dayItems = items.filter(i => i.day === dayNumber && !i.is_alternate);
  const dayAlternates = alternates.filter(a => a.day === dayNumber);
  const proposedCount = dayItems.filter(i => i.status === 'proposed').length;
  const approvedCount = dayItems.filter(i => i.status === 'approved' || i.status === 'booked').length;
  
  return (
    <div 
      id={`day-${dayNumber}`}
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
        isLocked ? 'border-[#8DA399]/40' : 'border-[#E6E4DE]'
      }`}
      data-testid={`smart-day-card-${dayNumber}`}
    >
      {/* Day Header */}
      <div className={`px-4 sm:px-6 py-4 ${isLocked ? 'bg-[#8DA399]/5' : 'bg-[#F9F8F4]'} border-b border-[#E6E4DE]`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-serif ${
              isLocked ? 'bg-[#8DA399] text-white' : 'bg-[#1A1A1A] text-white'
            }`}>
              {dayNumber}
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-xl text-[#1A1A1A]">Day {dayNumber}</h3>
                <span className={`px-2 py-0.5 text-xs rounded-full border ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
                {isLocked && (
                  <Lock className="w-4 h-4 text-[#8DA399]" />
                )}
              </div>
              <p className="text-sm text-[#8C8C8C]">{formattedDate}</p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-[#8C8C8C]">
              {approvedCount}/{dayItems.length} approved
            </span>
            
            {dayItems.length >= 2 && !isLocked && (
              <Button 
                size="sm"
                variant="outline"
                onClick={() => onOptimizeRoute(dayNumber)}
                className="border-[#C8A85A] text-[#C8A85A] hover:bg-[#C8A85A]/10"
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                Optimize
              </Button>
            )}
            
            {proposedCount > 0 && !isLocked && (
              <Button 
                size="sm" 
                onClick={() => onStatusChange(dayNumber, 'approved')}
                className="bg-[#8DA399] hover:bg-[#7A9389]"
              >
                <Check className="w-4 h-4 mr-1" />
                Approve Day
              </Button>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onLockToggle(dayNumber)}
              title={isLocked ? 'Unlock to edit' : 'Lock day'}
            >
              {isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Checklist */}
        <div className="mt-3">
          <DayChecklist checklist={checklist} />
        </div>
        
        {/* Meal Toggles */}
        <div className="flex gap-2 mt-3">
          {Object.entries(MEAL_SLOTS).map(([key, slot]) => (
            <button
              key={key}
              onClick={() => setMealSlots(prev => ({ ...prev, [key]: !prev[key] }))}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${
                mealSlots[key] 
                  ? `${slot.color} ${slot.accent} border` 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <slot.icon className="w-3 h-3" />
              {slot.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Day Content */}
      {!isLocked ? (
        <div className="p-3 sm:p-4 space-y-4">
          {/* Meal Slots (if enabled) */}
          {Object.entries(MEAL_SLOTS).map(([key, slot]) => (
            mealSlots[key] && (
              <TimeBlockSection
                key={key}
                blockKey={key}
                block={{ ...slot, color: slot.color, accent: slot.accent }}
                items={dayItems.filter(i => i.meal_type === key || i.section === key)}
                alternates={dayAlternates.filter(a => a.meal_type === key || a.section === key)}
                dayNumber={dayNumber}
                onAddItem={onAddItem}
                onUpdateItem={onUpdateItem}
                onDeleteItem={onDeleteItem}
                onApproveItem={onApproveItem}
                onSetIntentionallyOpen={onSetIntentionallyOpen}
              />
            )
          ))}
          
          {/* Time Blocks */}
          {Object.entries(TIME_BLOCKS).map(([key, block]) => (
            <TimeBlockSection
              key={key}
              blockKey={key}
              block={block}
              items={dayItems.filter(i => i.section === key && !i.meal_type)}
              alternates={dayAlternates.filter(a => a.section === key && !a.meal_type)}
              dayNumber={dayNumber}
              onAddItem={onAddItem}
              onUpdateItem={onUpdateItem}
              onDeleteItem={onDeleteItem}
              onApproveItem={onApproveItem}
              onSetIntentionallyOpen={onSetIntentionallyOpen}
            />
          ))}
        </div>
      ) : (
        <div className="p-6 text-center bg-[#8DA399]/5">
          <Lock className="w-8 h-8 text-[#8DA399] mx-auto mb-2" />
          <p className="text-sm text-[#8DA399]">Day is locked. Click unlock to edit.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN SMART ITINERARY BUILDER
// ============================================================
export function SmartItineraryBuilder({ tripId, trip, planningState, startDate, endDate, onRefresh }) {
  const [items, setItems] = useState([]);
  const [alternates, setAlternates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentDay, setCurrentDay] = useState(1);
  const [dayStatuses, setDayStatuses] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [localPlanningState, setLocalPlanningState] = useState(planningState || {});
  
  // Calculate total days from dates OR use planningState
  const calculateTotalDays = () => {
    // First try planningState
    if (planningState?.total_days > 0) {
      return planningState.total_days;
    }
    if (localPlanningState?.total_days > 0) {
      return localPlanningState.total_days;
    }
    // Calculate from dates
    if (startDate && endDate) {
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        return days > 0 ? days : 7;
      } catch (e) {
        return 7;
      }
    }
    return 7;
  };
  
  const totalDays = calculateTotalDays();
  const lockedDays = planningState?.locked_days || localPlanningState?.locked_days || {};
  
  // Load planning state if not provided
  useEffect(() => {
    const fetchPlanningState = async () => {
      if (!tripId || planningState) return;
      try {
        const res = await axios.get(`${API}/travels/trips/${tripId}/ai-planning-state`);
        setLocalPlanningState(res.data);
      } catch (e) {
        console.error('Failed to load planning state:', e);
      }
    };
    fetchPlanningState();
  }, [tripId, planningState]);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Calculate completeness for all days
  const dayCompleteness = useMemo(() => {
    const result = {};
    for (let day = 1; day <= totalDays; day++) {
      const dayItems = items.filter(i => i.day === day);
      result[day] = calculateDayCompleteness(dayItems, day, totalDays);
    }
    return result;
  }, [items, totalDays]);
  
  // Load itinerary
  useEffect(() => {
    if (tripId) loadItems();
  }, [tripId]);
  
  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travels/trip-itinerary/${tripId}`);
      const days = res.data || [];
      
      const allItems = [];
      const allAlternates = [];
      const statuses = {};
      
      days.forEach((day, idx) => {
        const dayNumber = day.day_number || idx + 1;
        statuses[dayNumber] = day.status || 'draft';
        
        // Process all time blocks and meal slots
        const sections = [...Object.keys(TIME_BLOCKS), ...Object.keys(MEAL_SLOTS)];
        sections.forEach(section => {
          const sectionItems = day[section] || [];
          sectionItems.forEach((item, order) => {
            const processedItem = {
              ...item,
              id: item.id || `${dayNumber}-${section}-${order}`,
              day: dayNumber,
              section: section,
              order: order,
              status: item.status || (item.added_by === 'Trip AI' ? 'proposed' : 'approved'),
              meal_type: Object.keys(MEAL_SLOTS).includes(section) ? section : null
            };
            
            if (item.is_alternate) {
              allAlternates.push(processedItem);
            } else {
              allItems.push(processedItem);
            }
          });
        });
      });
      
      setItems(allItems);
      setAlternates(allAlternates);
      setDayStatuses(statuses);
    } catch (e) {
      console.error('Failed to load itinerary:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  const saveItems = useCallback(async (updatedItems, updatedAlternates = alternates) => {
    setSaving(true);
    try {
      const daysMap = {};
      
      // Process main items
      [...updatedItems, ...updatedAlternates].forEach(item => {
        const dayNum = item.day || 1;
        if (!daysMap[dayNum]) {
          daysMap[dayNum] = { 
            day_number: dayNum, 
            status: dayStatuses[dayNum] || 'draft',
            ...Object.fromEntries([...Object.keys(TIME_BLOCKS), ...Object.keys(MEAL_SLOTS)].map(k => [k, []]))
          };
        }
        const section = item.section || 'afternoon';
        if (!daysMap[dayNum][section]) {
          daysMap[dayNum][section] = [];
        }
        daysMap[dayNum][section].push(item);
      });
      
      const days = Object.values(daysMap).sort((a, b) => a.day_number - b.day_number);
      
      await axios.put(`${API}/travels/itinerary/${tripId}`, { days });
      setItems(updatedItems);
      setAlternates(updatedAlternates);
      
      // Notify itinerary update
      window.dispatchEvent(new CustomEvent('itinerary-updated', { detail: { tripId, days } }));
      
    } catch (e) {
      console.error('Failed to save itinerary:', e);
      toast.error('Failed to save');
      throw e;
    } finally {
      setSaving(false);
    }
  }, [alternates, dayStatuses, tripId]);
  
  // Item handlers
  const handleAddItem = useCallback((dayNumber, section) => {
    const newItem = {
      id: `new-${Date.now()}`,
      day: dayNumber,
      section: section,
      title: '',
      type: section === 'breakfast' || section === 'lunch' || section === 'dinner' ? 'restaurant' : 'activity',
      status: 'draft',
      meal_type: Object.keys(MEAL_SLOTS).includes(section) ? section : null,
      order: items.filter(i => i.day === dayNumber && i.section === section).length
    };
    const updated = [...items, newItem];
    setItems(updated);
    saveItems(updated);
  }, [items, saveItems]);
  
  const handleUpdateItem = useCallback(async (itemId, updates) => {
    const updated = items.map(i => i.id === itemId ? { ...i, ...updates } : i);
    await saveItems(updated);
  }, [items, saveItems]);
  
  const handleDeleteItem = useCallback(async (itemId) => {
    if (!confirm('Delete this item?')) return;
    const updated = items.filter(i => i.id !== itemId);
    await saveItems(updated);
    toast.success('Item deleted');
  }, [items, saveItems]);
  
  const handleApproveItem = useCallback(async (itemId) => {
    const updated = items.map(i => i.id === itemId ? { ...i, status: 'approved' } : i);
    await saveItems(updated);
    toast.success('Item approved');
  }, [items, saveItems]);
  
  const handleSetIntentionallyOpen = useCallback(async (itemId, value) => {
    const updated = items.map(i => i.id === itemId ? { ...i, intentionally_open: value } : i);
    await saveItems(updated);
  }, [items, saveItems]);
  
  // Day handlers
  const handleDayStatusChange = useCallback(async (dayNumber, newStatus) => {
    setDayStatuses(prev => ({ ...prev, [dayNumber]: newStatus }));
    
    if (newStatus === 'approved') {
      // Approve all proposed items in this day
      const updated = items.map(i => 
        i.day === dayNumber && i.status === 'proposed' ? { ...i, status: 'approved' } : i
      );
      await saveItems(updated);
      toast.success(`Day ${dayNumber} approved`);
    }
    
    onRefresh?.();
  }, [items, saveItems, onRefresh]);
  
  const handleLockToggle = useCallback(async (dayNumber) => {
    const isCurrentlyLocked = lockedDays[String(dayNumber)] || lockedDays[dayNumber];
    
    if (isCurrentlyLocked) {
      try {
        await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/unlock-day/${dayNumber}`);
        toast.success(`Day ${dayNumber} unlocked`);
        onRefresh?.();
      } catch (e) {
        toast.error('Failed to unlock day');
      }
    } else {
      try {
        await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/apply-day`, { day_number: dayNumber });
        toast.success(`Day ${dayNumber} locked`);
        onRefresh?.();
      } catch (e) {
        toast.error('Failed to lock day');
      }
    }
  }, [lockedDays, tripId, onRefresh]);
  
  // Route optimization
  const handleOptimizeRoute = useCallback(async (dayNumber) => {
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai/optimize-day-route`, {
        day_number: dayNumber,
        optimize_for: 'efficiency'
      });
      
      if (res.data.success && res.data.optimization?.optimized_order) {
        toast.success(`Route optimized for Day ${dayNumber}`);
        // Apply optimization
        await axios.post(`${API}/travels/trips/${tripId}/ai/apply-route-optimization`, {
          day_number: dayNumber,
          optimized_order: res.data.optimization.optimized_order
        });
        loadItems();
      } else {
        toast.info(res.data.message || 'No optimization needed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to optimize route');
    }
  }, [tripId, loadItems]);
  
  // DnD handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveItem(items.find(i => i.id === active.id) || alternates.find(a => a.id === active.id));
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);
    
    if (!over) return;
    
    // Handle reordering logic here
    // ... (similar to existing ItineraryBuilder)
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A]" />
      </div>
    );
  }
  
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="min-h-screen bg-[#FAFAF8]" data-testid="smart-itinerary-builder">
        {/* Day Navigator (Sticky) */}
        <DayNavigator
          totalDays={totalDays}
          startDate={getTripStartDate(trip)}
          currentDay={currentDay}
          onDaySelect={setCurrentDay}
          dayStatuses={dayStatuses}
          dayCompleteness={dayCompleteness}
        />
        
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 bg-white border-b border-[#E6E4DE]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="font-serif text-2xl text-[#1A1A1A]">Smart Itinerary</h2>
              <p className="text-sm text-[#8C8C8C]">
                {totalDays} days • {items.filter(i => i.status === 'approved').length}/{items.length} approved
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {saving && (
                <span className="text-xs text-[#8C8C8C] flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
              <Button variant="outline" size="sm" onClick={loadItems}>
                Refresh
              </Button>
            </div>
          </div>
        </div>
        
        {/* Day Cards */}
        <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto">
          {Array.from({ length: totalDays }, (_, i) => i + 1).map(dayNum => (
            <DayCard
              key={dayNum}
              dayNumber={dayNum}
              totalDays={totalDays}
              startDate={getTripStartDate(trip)}
              items={items}
              alternates={alternates}
              status={dayStatuses[dayNum] || 'draft'}
              isLocked={lockedDays[String(dayNum)] || lockedDays[dayNum]}
              onStatusChange={handleDayStatusChange}
              onLockToggle={handleLockToggle}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
              onApproveItem={handleApproveItem}
              onSetIntentionallyOpen={handleSetIntentionallyOpen}
              onOptimizeRoute={handleOptimizeRoute}
              checklist={dayCompleteness[dayNum] || {}}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {totalDays === 0 && (
          <div className="text-center py-20 bg-[#F9F8F4] rounded-2xl mx-4 sm:mx-6">
            <Calendar className="w-12 h-12 text-[#C8A85A]/30 mx-auto mb-4" />
            <h3 className="font-medium text-[#1A1A1A] mb-2">No trip dates set</h3>
            <p className="text-sm text-[#8C8C8C]">Set the trip dates to start building the itinerary</p>
          </div>
        )}
      </div>
      
      {/* Drag Overlay */}
      <DragOverlay>
        {activeItem && (
          <div className="bg-white rounded-xl shadow-2xl border border-[#C8A85A] p-3 opacity-90">
            <p className="font-medium">{activeItem.title || 'Untitled'}</p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default SmartItineraryBuilder;
